import React, { useEffect, useMemo, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { useForm } from 'react-hook-form';
import {
  ErrorField,
  StyledSelect,
} from '~/lib/HooksFormFields';
import {
  useGetEstimatesList,
  useUpdateEstimateSharedWith,
} from '~/hooks/estimates';
import {
  EstimateShareAccess,
  IEstimate,
  IEstimateShare,
} from '~/types/estimates';
import { IOption } from '~/types/options';
import styles from './share-estimate.module.scss';

const ACCESS_OPTIONS: IOption[] = [
  { label: 'Lecture', value: 'read' },
  { label: 'Édition', value: 'edit' },
];

const getUserLabel = (share: IEstimateShare) => {
  const { profile } = share.user;
  if (profile?.firstName || profile?.lastName) {
    return `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();
  }
  return share.user.email || 'Utilisateur';
};

const ShareEstimate = ({
  estimate,
  close,
}: {
  estimate: IEstimate,
  close: () => void,
}) => {
  const [sharedWith, setSharedWith] = useState<IEstimateShare[]>(estimate.sharedWith || []);
  const { data: list = {} } = useGetEstimatesList();
  const { commercialUsers = [] } = list;
  const {
    isLoading,
    mutateAsync: updateSharedWith,
  } = useUpdateEstimateSharedWith('Options de partage mises à jour');

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user: null as string | null,
      access: 'read' as EstimateShareAccess,
    },
  });

  useEffect(() => {
    setSharedWith(estimate.sharedWith || []);
  }, [estimate.sharedWith]);

  const authorId = estimate.author?._id;
  const sharedUserIds = useMemo(
    () => sharedWith.map((share) => share.user._id),
    [sharedWith],
  );

  const availableUsers = useMemo(
    () => commercialUsers.filter(
      (user: IOption) => String(user.value) !== authorId
        && !sharedUserIds.includes(String(user.value)),
    ),
    [commercialUsers, authorId, sharedUserIds],
  );

  const selectedUser = watch('user');
  const selectedAccess = watch('access');

  const handleAdd = () => {
    if (!selectedUser) return;

    const userOption = commercialUsers.find(
      (user: IOption) => String(user.value) === selectedUser,
    );
    const [firstName = '', ...lastNameParts] = (userOption?.label || '').split(' ');

    setSharedWith((current) => ([
      ...current,
      {
        user: {
          _id: selectedUser,
          profile: {
            firstName,
            lastName: lastNameParts.join(' '),
          },
        },
        access: selectedAccess,
      },
    ]));
    reset({ user: null, access: 'read' });
  };

  const handleAccessChange = (userId: string, access: EstimateShareAccess) => {
    setSharedWith((current) => current.map((share) => (
      share.user._id === userId ? { ...share, access } : share
    )));
  };

  const handleRemove = (userId: string) => {
    setSharedWith((current) => current.filter((share) => share.user._id !== userId));
  };

  const onSave = async () => {
    try {
      await updateSharedWith({
        estimateId: estimate._id,
        sharedWith: sharedWith.map((share) => ({
          user: share.user._id as string,
          access: share.access,
        })),
      });
      close();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.form}>
      <h3>Options de partage</h3>

      <div className={styles.list}>
        {sharedWith.length === 0
          ? <p className={styles.empty}>Aucun partage pour ce devis.</p>
          : sharedWith.map((share) => (
            <div className={styles.row} key={share.user._id}>
              <p className={styles.name}>{getUserLabel(share)}</p>
              <select
                value={share.access}
                onChange={(event) => {
                  handleAccessChange(
                    share.user._id as string,
                    event.target.value as EstimateShareAccess,
                  );
                }}
              >
                {ACCESS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => handleRemove(share.user._id as string)}>
                ×
              </button>
            </div>
          ))
        }
      </div>

      <div className={styles.add}>
        <div className={styles.containerField}>
          <StyledSelect
            name="user"
            label="Ajouter un utilisateur"
            control={control}
            options={availableUsers}
            isSearchable
            isClearable
          />
          {errors.user?.message && <ErrorField message={errors.user?.message} />}
        </div>
        <div className={styles.containerField}>
          <StyledSelect
            name="access"
            label="Accès"
            control={control}
            options={ACCESS_OPTIONS}
          />
        </div>
        <button type="button" onClick={handleAdd} disabled={!selectedUser}>
          Ajouter
        </button>
      </div>

      <div className={styles.buttons}>
        <button type="button" className="invisible" onClick={() => close()}>Annuler</button>
        <button type="button" disabled={isLoading} onClick={onSave}>
          <span className="icon">{BsCheck({})}</span>
          <span>Enregistrer</span>
        </button>
      </div>
    </div>
  );
};

export default ShareEstimate;
